/*
  Montredo world clock (sections/world-clock.liquid).
  The markup and all static text are rendered by Liquid. This script only fills times, hands,
  day phases and planner sentences.
  One shared timer for all instances, aligned to the full second and paused while the tab is hidden.
  Sun events, the night shading and day phases are recomputed once a minute.
*/
(() => {
  if (window.customElements.get('world-clock')) return;

  const instances = new Set();
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  let timer = null;
  let lastMinute = -1;

  /* Time zone helpers */

  const formatters = new Map();

  function formatter(locale, tz, options) {
    const key = locale + '|' + tz + '|' + JSON.stringify(options);
    let f = formatters.get(key);
    if (!f) {
      f = new Intl.DateTimeFormat(locale, Object.assign({ timeZone: tz }, options));
      formatters.set(key, f);
    }
    return f;
  }

  const PARTS_OPTIONS = { hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' };

  /* Canonical IANA name as the browser resolves it (Asia/Kolkata and Asia/Calcutta compare equal), or null. */
  function canonicalZone(tz) {
    if (!tz) return null;
    try {
      return new Intl.DateTimeFormat('en', { timeZone: tz }).resolvedOptions().timeZone;
    } catch (error) {
      return null;
    }
  }

  /* Offset of a zone against UTC in minutes, at the given instant. */
  function offsetMin(tz, now) {
    const p = {};
    formatter('en-GB', tz, PARTS_OPTIONS).formatToParts(now).forEach((x) => { p[x.type] = x.value; });
    const asUtc = Date.UTC(+p.year, +p.month - 1, +p.day, (+p.hour) % 24, +p.minute, +p.second);
    return Math.round((asUtc - Math.floor(now / 1000) * 1000) / 60000);
  }

  const pad = (n) => (n < 10 ? '0' : '') + n;

  function wall(now, off, minuteOnly) {
    const t = new Date(now + off * 60000);
    const H = t.getUTCHours();
    const M = t.getUTCMinutes();
    const S = t.getUTCSeconds();
    return {
      H, M,
      time: pad(H) + ':' + pad(M) + ':' + pad(S),
      hm: pad(H) + ':' + pad(M),
      hDeg: ((H % 12) + M / 60) * 30,
      mDeg: (M + (minuteOnly ? 0 : S / 60)) * 6,
      sDeg: S * 6,
      dayNo: Math.floor((now + off * 60000) / 864e5)
    };
  }

  /* Sun position (low precision, good to about a minute) */

  function sun(ms) {
    const r = Math.PI / 180;
    const d = ms / 864e5 - 10957.5;
    const g = (357.529 + 0.98560028 * d) * r;
    const q = 280.459 + 0.98564736 * d;
    const L = (q + 1.915 * Math.sin(g) + 0.02 * Math.sin(2 * g)) * r;
    const e = (23.439 - 0.00000036 * d) * r;
    const ra = Math.atan2(Math.cos(e) * Math.sin(L), Math.cos(L)) / r / 15;
    const dec = Math.asin(Math.sin(e) * Math.sin(L));
    let eqt = q / 15 - ra;
    eqt = ((eqt % 24) + 36) % 24 - 12;
    let lon = -15 * ((ms / 36e5) % 24 - 12 + eqt);
    lon = (((lon + 180) % 360) + 360) % 360 - 180;
    return { dec, lon };
  }

  function elevation(lat, lon, ms) {
    const s = sun(ms);
    const r = Math.PI / 180;
    return Math.asin(Math.sin(lat * r) * Math.sin(s.dec) + Math.cos(lat * r) * Math.cos(s.dec) * Math.cos((lon - s.lon) * r)) / r;
  }

  /* Next sunrise or sunset: 5-minute steps to find the crossing, then bisection to about 5 seconds. */
  function nextSunEvent(city, now) {
    const isUp = (t) => elevation(city.lat, city.lon, t) > -0.833;
    const up = isUp(now);
    for (let i = 1; i <= 300; i++) {
      let hi = now + i * 300000;
      if (isUp(hi) !== up) {
        let lo = hi - 300000;
        for (let j = 0; j < 6; j++) {
          const mid = (lo + hi) / 2;
          if (isUp(mid) === up) lo = mid; else hi = mid;
        }
        return { up, t: hi };
      }
    }
    return null;
  }

  function nightPath(now) {
    const s = sun(now);
    const r = Math.PI / 180;
    let t = Math.tan(s.dec);
    if (Math.abs(t) < 1e-3) t = t < 0 ? -1e-3 : 1e-3;
    const pts = [];
    for (let lon = -180; lon <= 180; lon += 3) {
      const lat = Math.atan(-Math.cos((lon - s.lon) * r) / t) / r;
      pts.push((lon + 180).toFixed(1) + ' ' + (90 - lat).toFixed(2));
    }
    const pole = s.dec > 0 ? 180 : 0;
    return 'M ' + pts.join(' L ') + ' L 360 ' + pole + ' L 0 ' + pole + ' Z';
  }

  /* Text helpers */

  function fill(template, values) {
    return String(template || '').replace(/\[(\w+)\]/g, (match, key) => (key in values ? values[key] : match));
  }

  /* Locale strings come through the Liquid t filter, which may HTML-escape characters such as apostrophes. */
  function decodeEntities(value) {
    if (typeof value !== 'string' || value.indexOf('&') === -1) return value;
    const area = document.createElement('textarea');
    area.innerHTML = value;
    return area.value;
  }

  /* Empty values keep a no-break space so placeholders never collapse and shift the layout. */
  function setText(el, text) {
    const value = text === '' || text == null ? '\u00a0' : String(text);
    if (el && el.textContent !== value) el.textContent = value;
  }

  /* Local wall-clock date and minute as a valid datetime attribute value, e.g. 2026-10-09T13:45. */
  function localDateTime(now, off) {
    return new Date(now + off * 60000).toISOString().slice(0, 16);
  }

  function setDateTime(el, value) {
    if (el && el.getAttribute('datetime') !== value) el.setAttribute('datetime', value);
  }

  /* Shared timer */

  function schedule() {
    clearTimeout(timer);
    timer = null;
    if (document.hidden || !instances.size) return;
    timer = setTimeout(tick, 1000 - (Date.now() % 1000) + 8);
  }

  function tick() {
    const now = Date.now();
    const minute = Math.floor(now / 60000);
    const minuteChanged = minute !== lastMinute;
    lastMinute = minute;
    instances.forEach((instance) => instance.update(now, minuteChanged));
    schedule();
  }

  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      clearTimeout(timer);
      timer = null;
    } else {
      lastMinute = -1;
      tick();
    }
  });

  class WorldClock extends HTMLElement {
    connectedCallback() {
      if (!this.ready) this.ready = this.setup();
      if (!this.ready) return;
      instances.add(this);
      this.classList.add('is-live');
      this.update(Date.now(), true);
      requestAnimationFrame(() => this.classList.add('is-settled'));
      if (!timer) schedule();
    }

    disconnectedCallback() {
      instances.delete(this);
      if (!instances.size) {
        clearTimeout(timer);
        timer = null;
      }
    }

    setup() {
      const configEl = this.querySelector('[data-world-clock-config]');
      if (!configEl) return false;

      let config;
      try {
        config = JSON.parse(configEl.textContent);
      } catch (error) {
        return false;
      }

      this.config = config;
      this.locale = config.locale || document.documentElement.lang || 'en';
      try {
        new Intl.DateTimeFormat(this.locale);
      } catch (error) {
        this.locale = 'en';
      }
      this.strings = {};
      Object.keys(config.strings || {}).forEach((key) => { this.strings[key] = decodeEntities(config.strings[key]); });
      this.templates = config.templates || {};
      this.schemeClasses = [config.schemes.day, config.schemes.twilight, config.schemes.night];

      this.homeTz = canonicalZone(Intl.DateTimeFormat().resolvedOptions().timeZone) || 'UTC';

      this.cities = [];
      (config.cities || []).forEach((c) => {
        const tz = canonicalZone(c.tz);
        if (tz) {
          this.cities.push({ id: c.id, name: c.name, tz, lat: Number(c.lat) || 0, lon: Number(c.lon) || 0 });
          return;
        }
        /* Unknown time zone ID: hide the server-rendered card and pin instead of showing placeholders. */
        this.querySelectorAll(`[data-world-clock-city="${c.id}"], [data-world-clock-pin="${c.id}"]`).forEach((el) => { el.hidden = true; });
        if (config.designMode) console.warn(`World clock: "${c.tz}" is not a valid time zone ID (city "${c.name}"). Use an IANA name such as Europe/London.`);
      });

      this.homeCity = this.cities.find((c) => c.tz === this.homeTz) || null;
      this.homeName = this.homeCity ? this.homeCity.name : this.homeTz.split('/').pop().replace(/_/g, ' ');
      this.home = { id: 'home', name: this.homeName, tz: this.homeTz };

      /* Hero */
      this.hero = {
        hands: this.handRefs(this.querySelector('[data-world-clock-hero-dial]')),
        time: this.querySelector('[data-world-clock-hero-time]'),
        where: this.querySelector('[data-world-clock-hero-where]')
      };

      /* City cards and map pins */
      this.cityRefs = this.cities.map((city) => {
        const card = this.querySelector(`[data-world-clock-city="${city.id}"]`);
        const pin = this.querySelector(`[data-world-clock-pin="${city.id}"]`);
        const isHome = city.tz === this.homeTz;

        if (card && isHome) {
          card.classList.add('is-home');
          card.querySelectorAll('[data-world-clock-home]').forEach((el) => { el.hidden = false; });
        }
        if (pin && isHome) pin.classList.add('is-home');

        return {
          city,
          card,
          pin,
          hands: card ? this.handRefs(card) : null,
          clock: card && card.querySelector('[data-world-clock-clock]'),
          day: card && card.querySelector('[data-world-clock-day]'),
          abbr: card && card.querySelector('[data-world-clock-abbr]'),
          phase: card && card.querySelector('[data-world-clock-phase]'),
          diff: card && card.querySelector('[data-world-clock-diff]'),
          sunEvent: card && card.querySelector('[data-world-clock-sun-event]'),
          dot: pin && pin.querySelector('.world-clock__dot'),
          pinTime: pin && pin.querySelector('[data-world-clock-pin-time]')
        };
      });

      /* Map */
      this.map = {
        night: this.querySelector('[data-world-clock-night]'),
        sun: this.querySelector('[data-world-clock-sun]'),
        summary: this.querySelector('[data-world-clock-summary]')
      };

      this.setupPlanner();
      return true;
    }

    setupPlanner() {
      const from = this.querySelector('select[name="from"]');
      const to = this.querySelector('select[name="to"]');
      if (!from || !to) {
        this.planner = null;
        return;
      }

      this.planner = {
        from,
        to,
        route: this.querySelector('[data-world-clock-route]'),
        headline: this.querySelector('[data-world-clock-headline]'),
        fromName: this.querySelector('[data-world-clock-from-name]'),
        toName: this.querySelector('[data-world-clock-to-name]'),
        fromTime: this.querySelector('[data-world-clock-from-time]'),
        toTime: this.querySelector('[data-world-clock-to-time]'),
        any: this.querySelector('[data-world-clock-any]'),
        gmt: this.querySelector('[data-world-clock-gmt]'),
        date: this.querySelector('[data-world-clock-date]'),
        tip: this.querySelector('[data-world-clock-tip]')
      };

      /* Name the visitor's own zone in the "Your time" options. */
      [from, to].forEach((select) => {
        const homeOption = select.querySelector('option[value="home"]');
        if (homeOption) homeOption.textContent = fill(this.strings.yourTimeOption, { city: this.homeName });
      });

      /* Drop options whose time zone ID is invalid. */
      const valid = new Set(this.cities.map((c) => c.id));
      [from, to].forEach((select) => {
        Array.from(select.options).forEach((option) => {
          if (option.value !== 'home' && !valid.has(option.value)) option.remove();
        });
      });

      /* The default destination must be a city whose time differs from the visitor's time. */
      const now = Date.now();
      const homeOff = offsetMin(this.homeTz, now);
      const current = this.cityById(to.value);
      if (!current || offsetMin(current.tz, now) === homeOff) {
        const other = this.cities.find((c) => offsetMin(c.tz, now) !== homeOff);
        if (other) to.value = other.id;
      }

      const onChange = () => this.plan(Date.now());
      from.addEventListener('change', onChange);
      to.addEventListener('change', onChange);

      const swap = this.querySelector('[data-world-clock-swap]');
      if (swap) {
        swap.addEventListener('click', () => {
          const f = from.value;
          from.value = to.value;
          to.value = f;
          this.plan(Date.now());
        });
      }

      this.setupTipLink();
    }

    /* Without a custom link, the date tip opens the first question of the FAQ section on this page. */
    setupTipLink() {
      const link = this.querySelector('[data-world-clock-tip-link]');
      const targetId = this.config.faqTarget;
      if (!link || link.getAttribute('href') !== '#' + targetId) return;

      const target = document.querySelector('.faq accordion-disclosure');
      if (!target) {
        link.removeAttribute('href');
        link.hidden = true;
        return;
      }

      if (!target.id) target.id = targetId;
      link.setAttribute('href', '#' + target.id);
      link.addEventListener('click', () => {
        if (typeof target.open === 'function') {
          target.open();
        } else {
          const details = target.querySelector('details');
          if (details) details.open = true;
        }
        /* Move keyboard focus to the opened question. */
        const summary = target.querySelector('summary');
        if (summary) summary.focus();
      });
    }

    cityById(id) {
      return this.cities.find((c) => c.id === id) || null;
    }

    /* A planner value is either "home" (the visitor's own zone) or a city block id. */
    resolve(id) {
      return id === 'home' ? this.home : this.cityById(id);
    }

    handRefs(root) {
      if (!root) return null;
      return {
        h: root.querySelector('[data-hand="h"]'),
        m: root.querySelector('[data-hand="m"]'),
        s: root.querySelector('[data-hand="s"]')
      };
    }

    setHands(refs, w) {
      if (!refs || !refs.h) return;
      refs.h.setAttribute('transform', `rotate(${w.hDeg} 50 50)`);
      refs.m.setAttribute('transform', `rotate(${w.mDeg} 50 50)`);
      refs.s.setAttribute('transform', `rotate(${w.sDeg} 50 50)`);
    }

    spanText(mins) {
      const h = Math.floor(mins / 60);
      const m = mins % 60;
      const s = this.strings;
      const hours = fill(h === 1 ? s.hoursOne : s.hoursOther, { n: h });
      const minutes = fill(m === 1 ? s.minutesOne : s.minutesOther, { n: m });
      if (!m) return hours;
      return h ? hours + ' ' + minutes : minutes;
    }

    shortSpan(mins) {
      const h = Math.floor(mins / 60);
      const m = mins % 60;
      const parts = [];
      if (h) parts.push(fill(this.strings.hoursShort, { n: h }));
      if (m) parts.push(fill(this.strings.minutesShort, { n: m }));
      return parts.join(' ');
    }

    twelveHour(w) {
      const hour = w.H % 12 || 12;
      return w.M
        ? fill(this.strings.timeWithMinutes, { hour, minutes: pad(w.M), time: w.hm })
        : fill(this.strings.timeOnHour, { hour, time: w.hm });
    }

    longDate(tz, now) {
      return formatter(this.locale, tz, { weekday: 'long', day: 'numeric', month: 'long' }).format(now);
    }

    list(names) {
      if (!names.length) return this.strings.none;
      if (window.Intl && Intl.ListFormat) {
        try {
          return new Intl.ListFormat(this.locale, { type: 'conjunction' }).format(names);
        } catch (error) {
          /* Fall through to a plain list. */
        }
      }
      return names.join(', ');
    }

    update(now, minuteChanged) {
      /* Offsets only change on a minute boundary (DST switches happen on the minute), so cache them per minute. */
      if (minuteChanged || this.homeOff === undefined) {
        this.homeOff = offsetMin(this.homeTz, now);
        this.cityRefs.forEach((ref) => { ref.off = offsetMin(ref.city.tz, now); });
      }

      const minuteOnly = reducedMotion.matches;
      const homeOff = this.homeOff;
      const hw = wall(now, homeOff, minuteOnly);

      this.setHands(this.hero.hands, hw);
      setText(this.hero.time, hw.time);

      this.cityRefs.forEach((ref) => {
        const w = wall(now, ref.off, minuteOnly);
        ref.wall = w;
        if (ref.hands) this.setHands(ref.hands, w);
        setText(ref.clock, w.time);
        setText(ref.pinTime, w.hm);
      });

      if (minuteChanged) this.updateMinute(now, homeOff, hw);
    }

    updateMinute(now, homeOff, hw) {
      const s = this.strings;
      const groups = { day: [], twilight: [], night: [] };

      this.cityRefs.forEach((ref) => {
        const c = ref.city;
        const el = elevation(c.lat, c.lon, now);
        const key = el > -0.833 ? 'day' : (el > -6 ? 'twilight' : 'night');
        groups[key].push(c.name);

        if (ref.dot) ref.dot.className = 'world-clock__dot world-clock__dot--' + key;

        if (!ref.card) return;

        let phase = s.day;
        if (key === 'night') phase = s.night;
        if (key === 'twilight') phase = elevation(c.lat, c.lon, now + 600000) > el ? s.dawn : s.dusk;

        if (ref.card.dataset.phase !== key) {
          ref.card.classList.remove(...this.schemeClasses);
          ref.card.classList.add(this.config.schemes[key]);
          ref.card.dataset.phase = key;
        }
        setText(ref.phase, phase);

        const dm = ref.off - homeOff;
        setText(ref.diff, dm === 0 ? s.sameAsYours : (dm > 0 ? '+' : '−') + this.shortSpan(Math.abs(dm)));

        const dd = ref.wall.dayNo - hw.dayNo;
        const dayWord = dd === 0 ? s.today : (dd > 0 ? s.tomorrow : s.yesterday);
        setText(ref.day, dayWord + ', ' + formatter(this.locale, c.tz, { weekday: 'short', day: 'numeric', month: 'short' }).format(now));

        setDateTime(ref.clock, localDateTime(now, ref.off));

        const ev = nextSunEvent(c, now);
        setText(ref.sunEvent, ev ? fill(ev.up ? s.sunset : s.sunrise, { time: wall(ev.t, offsetMin(c.tz, ev.t)).hm }) : '');

        const abbr = formatter(this.locale, c.tz, { timeZoneName: 'short' }).formatToParts(now).find((x) => x.type === 'timeZoneName');
        setText(ref.abbr, abbr ? abbr.value : '');
      });

      if (this.map.night) this.map.night.setAttribute('d', nightPath(now));
      if (this.map.sun) {
        const sp = sun(now);
        this.map.sun.style.left = ((sp.lon + 180) / 360 * 100).toFixed(2) + '%';
        this.map.sun.style.top = ((90 - sp.dec * 180 / Math.PI) / 180 * 100).toFixed(2) + '%';
        this.map.sun.hidden = false;
      }
      setText(this.map.summary, fill(s.mapSummary, {
        day: this.list(groups.day),
        twilight: this.list(groups.twilight),
        night: this.list(groups.night)
      }));

      setText(this.hero.where, this.homeName + ' · ' + this.longDate(this.homeTz, now));
      setDateTime(this.hero.time, localDateTime(now, homeOff));

      this.plan(now);
    }

    plan(now) {
      const p = this.planner;
      if (!p) return;

      const t = this.templates;
      const fromCity = this.resolve(p.from.value);
      const toCity = this.resolve(p.to.value);
      if (!fromCity || !toCity) return;

      const fOff = offsetMin(fromCity.tz, now);
      const tOff = offsetMin(toCity.tz, now);
      const fw = wall(now, fOff);
      const tw = wall(now, tOff);
      const delta = tOff - fOff;
      const values = {
        from: fromCity.name,
        to: toCity.name,
        span: this.spanText(Math.abs(delta)),
        direction: delta > 0 ? this.strings.forward : this.strings.back,
        time: this.twelveHour(tw),
        date: this.longDate(toCity.tz, now)
      };
      const wholeHours = Math.abs(delta) % 60 === 0;

      setText(p.route, fromCity.name + ' → ' + toCity.name);
      const homeId = this.homeCity ? this.homeCity.id : 'home';
      /* "You are already in …" only when both sides are the visitor's own place. */
      const fromKey = fromCity.id === 'home' ? homeId : fromCity.id;
      const toKey = toCity.id === 'home' ? homeId : toCity.id;
      const sameCity = fromKey === toKey;
      const samePlace = sameCity && toKey === homeId;
      const cityName = toCity.id === 'home' ? this.homeName : toCity.name;
      let headline;
      if (samePlace) headline = fill(this.strings.samePlace, { city: cityName });
      else if (sameCity) headline = fill(this.strings.sameCity, { city: cityName });
      else headline = fill(delta === 0 ? t.headlineSame : (delta > 0 ? t.headlineAhead : t.headlineBehind), values);
      setText(p.headline, headline);
      setText(p.fromName, fromCity.name);
      setText(p.toName, toCity.name);
      setText(p.fromTime, fw.hm);
      setText(p.toTime, tw.hm);
      setDateTime(p.fromTime, localDateTime(now, fOff));
      setDateTime(p.toTime, localDateTime(now, tOff));
      /* Same city on both sides: no trip, so no "Enjoy the flight". */
      setText(p.any, sameCity ? this.strings.nothingToSet : fill(delta === 0 ? t.anySame : t.anyChange, values));
      setText(p.gmt, fill(delta === 0 ? t.gmtSame : (wholeHours ? t.gmtFull : t.gmtPartial), values));

      const dd = tw.dayNo - fw.dayNo;
      const dateText = fill(dd > 0 ? t.dateForward : (dd < 0 ? t.dateBack : t.dateSame), values);
      setText(p.date, dateText);
      if (p.tip) p.tip.hidden = dd === 0;

      /* "Your time" as destination marks the visitor's own pin, if their city is on the map. */
      const destId = toCity.id === 'home' && this.homeCity ? this.homeCity.id : toCity.id;
      this.cityRefs.forEach((ref) => {
        if (ref.pin) ref.pin.classList.toggle('is-dest', ref.city.id === destId);
      });

      /* Announce changes only after the first fill, so nothing is read out on page load. */
      if (p.headline && !p.headline.hasAttribute('aria-live')) p.headline.setAttribute('aria-live', 'polite');
    }
  }

  window.customElements.define('world-clock', WorldClock);
})();
